import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Loader2, RefreshCw, Play, CheckCircle2, Ban, MessageCircle, List, CalendarDays, ChevronLeft, ChevronRight, FileText, Fuel, Camera, ImagePlus, PenLine, Link2, Wrench, Gavel, Trash2, Check, Pencil, Paperclip } from "lucide-react";
import { formatBRL, waLink } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hoje = new Date().toISOString().slice(0, 10);

const STATUS = {
  reservada: { label: "Reservada", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30", cell: "bg-amber-500/40" },
  ativa: { label: "Ativa", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", cell: "bg-emerald-500/70" },
  devolvida: { label: "Devolvida", cls: "bg-slate-500/15 text-slate-400 border-slate-500/30", cell: "bg-slate-500/40" },
  cancelada: { label: "Cancelada", cls: "bg-red-500/15 text-red-400 border-red-500/30", cell: "" },
};

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const inputCls =
  "w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

export const LocacaoTab = ({ onUnauthorized }) => {
  const [rentals, setRentals] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [alertasManut, setAlertasManut] = useState({});
  const [finesFor, setFinesFor] = useState(null);
  const [fines, setFines] = useState([]);
  const [fineForm, setFineForm] = useState({ descricao: "", valor: "", data_infracao: hoje });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("lista");
  const [mesCal, setMesCal] = useState(hoje.slice(0, 7));
  const [checklistFor, setChecklistFor] = useState(null);
  const [chk, setChk] = useState({ combustivel: 50, observacao: "", fotos: [] });
  const [chkUploading, setChkUploading] = useState(false);
  const chkFileRef = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const load = useCallback(async () => {
    try {
      const [r, v, m] = await Promise.all([
        axios.get(`${API}/rentals`, { headers }),
        axios.get(`${API}/vehicles/admin`, { headers }),
        axios.get(`${API}/maintenance/alertas`, { headers }),
      ]);
      setRentals(r.data);
      setVehicles(v.data);
      setAlertasManut(m.data || {});
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else { setRentals([]); toast.error("Erro ao carregar locações."); }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vehicleName = (id) => vehicles.find((v) => v.id === id)?.nome || id;
  const locaveis = vehicles.filter((v) => v.para_locacao && v.status !== "vendido");

  const dias = form ? Math.max(1, Math.round((new Date(form.fim) - new Date(form.inicio)) / 86400000) || 1) : 0;
  const total = form ? dias * Number(form.diaria || 0) : 0;

  const setStatus = async (r, status) => {
    try {
      await axios.patch(`${API}/rentals/${r.id}`, { status }, { headers });
      setRentals((rs) => rs.map((x) => (x.id === r.id ? { ...x, status } : x)));
      if (status === "devolvida") toast.success("Devolvida! Receita lançada no financeiro e na ficha do carro.");
      else toast.success(`Locação ${STATUS[status].label.toLowerCase()}.`);
    } catch { toast.error("Erro ao atualizar locação."); }
  };

  const openChecklist = (r, tipo) => {
    setChecklistFor({ rental: r, tipo });
    setChk({ combustivel: 50, observacao: "", fotos: [] });
  };

  const chkFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setChkUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/upload`, fd, { headers });
        setChk((c) => ({ ...c, fotos: [...c.fotos, res.data.url] }));
      }
      toast.success("Fotos do checklist enviadas!");
    } catch { toast.error("Erro no upload das fotos."); }
    finally { setChkUploading(false); e.target.value = ""; }
  };

  const submitChecklist = async (pular = false) => {
    const { rental, tipo } = checklistFor;
    const novoStatus = tipo === "retirada" ? "ativa" : "devolvida";
    setSaving(true);
    try {
      if (!pular) {
        const res = await axios.post(`${API}/rentals/${rental.id}/checklist`, { tipo, ...chk }, { headers });
        setRentals((rs) => rs.map((x) => (x.id === rental.id ? res.data : x)));
      }
      await setStatus({ ...rental }, novoStatus);
      setChecklistFor(null);
    } catch { toast.error("Erro ao salvar checklist."); }
    finally { setSaving(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.id) {
        await axios.put(`${API}/rentals/${form.id}`, {
          cliente: form.cliente,
          telefone: form.telefone,
          inicio: form.inicio,
          fim: form.fim,
          diaria: Number(form.diaria),
          comprovantes: form.comprovantes || [],
        }, { headers });
        toast.success("Locação atualizada.");
      } else {
        await axios.post(`${API}/rentals`, { ...form, diaria: Number(form.diaria), comprovantes: form.comprovantes || [] }, { headers });
        toast.success("Locação criada.");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar locação.");
    } finally { setSaving(false); }
  };

  const [compUploading, setCompUploading] = useState(false);
  const compFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setCompUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/upload`, fd, { headers });
        setForm((fm) => ({ ...fm, comprovantes: [...(fm.comprovantes || []), res.data.url] }));
      }
      toast.success("Comprovante(s) anexado(s) — seguem junto para o Financeiro na finalização.");
    } catch { toast.error("Erro no upload do comprovante."); }
    finally { setCompUploading(false); e.target.value = ""; }
  };

  const openEdit = (r) => {
    setForm({ id: r.id, veiculo_id: r.veiculo_id, cliente: r.cliente, telefone: r.telefone, inicio: r.inicio, fim: r.fim, diaria: r.diaria, comprovantes: r.comprovantes || [] });
  };

  const removeRental = async (r) => {
    if (!window.confirm(`Excluir a locação de ${r.cliente} (${vehicleName(r.veiculo_id)})? Essa ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/rentals/${r.id}`, { headers });
      setRentals((rs) => rs.filter((x) => x.id !== r.id));
      toast.success("Locação excluída.");
    } catch { toast.error("Erro ao excluir locação."); }
  };

  const downloadContrato = async (r) => {
    try {
      const res = await axios.get(`${API}/rentals/${r.id}/contrato`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrato-locacao-${r.cliente.split(" ")[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Contrato baixado!");
    } catch { toast.error("Erro ao gerar contrato."); }
  };

  const fmtData = (d) => d?.split("-").reverse().join("/");

  const linkAssinatura = (r) => `${window.location.origin}/assinar/${r.id}`;

  const copiarLink = async (r) => {
    try {
      await navigator.clipboard.writeText(linkAssinatura(r));
      toast.success("Link de assinatura copiado!");
    } catch { toast.error("Não foi possível copiar o link."); }
  };

  const openFines = async (r) => {
    setFinesFor(r);
    setFineForm({ descricao: "", valor: "", data_infracao: hoje });
    try {
      const res = await axios.get(`${API}/fines?rental_id=${r.id}`, { headers });
      setFines(res.data);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao carregar multas.");
    }
  };

  const addFine = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/fines`, {
        rental_id: finesFor.id,
        descricao: fineForm.descricao,
        valor: Number(fineForm.valor),
        data_infracao: fineForm.data_infracao,
      }, { headers });
      setFines((fs) => [res.data, ...fs]);
      setFineForm({ descricao: "", valor: "", data_infracao: hoje });
      toast.success("Multa registrada — cobrança pendente lançada no Financeiro.");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao registrar multa.");
    } finally { setSaving(false); }
  };

  const toggleFine = async (f) => {
    const novo = f.status === "paga" ? "pendente" : "paga";
    try {
      await axios.patch(`${API}/fines/${f.id}`, { status: novo }, { headers });
      setFines((fs) => fs.map((x) => (x.id === f.id ? { ...x, status: novo } : x)));
    } catch { toast.error("Erro ao atualizar multa."); }
  };

  const removeFine = async (id) => {
    try {
      await axios.delete(`${API}/fines/${id}`, { headers });
      setFines((fs) => fs.filter((x) => x.id !== id));
      toast.success("Multa removida.");
    } catch { toast.error("Erro ao remover multa."); }
  };

  const waMulta = (r, f) => waLink(
    `Olá ${r.cliente}! Identificamos uma multa de trânsito durante a sua locação do ${vehicleName(r.veiculo_id)} (${fmtData(r.inicio)} a ${fmtData(r.fim)}): ${f.descricao} — ${formatBRL(f.valor)}. Conforme o contrato de locação (cláusula 2.3), seguimos com a cobrança. Pode responder por aqui para combinarmos o pagamento. Obrigado!`
  );

  const [anoCal, mesNum] = mesCal.split("-").map(Number);
  const diasNoMes = new Date(anoCal, mesNum, 0).getDate();
  const days = [...Array(diasNoMes)].map((_, i) => i + 1);
  const navMes = (delta) => {
    const d = new Date(anoCal, mesNum - 1 + delta, 1);
    setMesCal(d.toISOString().slice(0, 7));
  };
  const rentalNoDia = (vid, iso) =>
    (rentals || []).find((r) => r.veiculo_id === vid && r.status !== "cancelada" && r.inicio <= iso && r.fim >= iso);

  return (
    <div data-testid="locacao-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-2xl font-bold text-white">Locações por diária</h1>
        <div className="flex flex-wrap gap-2">
          <div className="glass rounded-full p-1 flex" data-testid="locacao-view-toggle">
            <button
              onClick={() => setView("lista")} data-testid="locacao-view-lista"
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${view === "lista" ? "bg-emerald-500 text-emerald-950" : "text-slate-300"}`}
            >
              <List className="w-3.5 h-3.5" /> Lista
            </button>
            <button
              onClick={() => setView("calendario")} data-testid="locacao-view-calendario"
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${view === "calendario" ? "bg-emerald-500 text-emerald-950" : "text-slate-300"}`}
            >
              <CalendarDays className="w-3.5 h-3.5" /> Calendário
            </button>
          </div>
          <button onClick={load} data-testid="locacao-refresh" className="inline-flex items-center gap-2 glass hover:border-emerald-500/50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
            <RefreshCw className="w-4 h-4" /> Atualizar
          </button>
          <button
            onClick={() => setForm({ veiculo_id: "", cliente: "", telefone: "", inicio: hoje, fim: hoje, diaria: "" })}
            data-testid="rental-new-button"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
          >
            <Plus className="w-4 h-4" /> Nova locação
          </button>
        </div>
      </div>

      <AnimatePresence>
        {form && (
          <motion.form
            onSubmit={save}
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="glass rounded-3xl p-6 sm:p-8 mb-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="rental-form"
          >
            <div className="sm:col-span-2 lg:col-span-3 flex items-center justify-between">
              <h2 className="font-display font-bold text-white">{form.id ? "Editar locação" : "Nova locação"}</h2>
              <button type="button" onClick={() => setForm(null)} className="text-slate-400 hover:text-white" data-testid="rental-form-close"><X className="w-5 h-5" /></button>
            </div>
            <select
              required className={inputCls} value={form.veiculo_id}
              disabled={!!form.id}
              onChange={(e) => {
                const v = vehicles.find((x) => x.id === e.target.value);
                setForm({ ...form, veiculo_id: e.target.value, diaria: v?.diaria || form.diaria });
              }}
              data-testid="rental-input-veiculo"
            >
              <option value="">Veículo*</option>
              {(form.id ? vehicles : locaveis).map((v) => <option key={v.id} value={v.id}>{v.nome} — {formatBRL(v.diaria || 0)}/dia</option>)}
            </select>
            <input className={inputCls} required placeholder="Cliente*" value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} data-testid="rental-input-cliente" />
            <input className={inputCls} placeholder="Telefone/WhatsApp" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="rental-input-telefone" />
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Retirada</label>
              <input className={inputCls} type="date" required value={form.inicio} onChange={(e) => setForm({ ...form, inicio: e.target.value })} data-testid="rental-input-inicio" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Devolução</label>
              <input className={inputCls} type="date" required value={form.fim} onChange={(e) => setForm({ ...form, fim: e.target.value })} data-testid="rental-input-fim" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Diária (R$) — editável</label>
              <input className={inputCls} type="number" required value={form.diaria} onChange={(e) => setForm({ ...form, diaria: e.target.value })} data-testid="rental-input-diaria" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="text-[11px] text-slate-500 block mb-1">Comprovantes de pagamento (foto ou PDF) — anexados à receita no Financeiro na finalização</label>
              <div className="flex flex-wrap items-center gap-2">
                <input type="file" accept="image/*,application/pdf" multiple id="rental-comprovantes" className="hidden" onChange={compFiles} data-testid="rental-input-comprovantes" />
                <label htmlFor="rental-comprovantes" data-testid="rental-comprovantes-button" className="inline-flex items-center gap-2 border border-dashed border-slate-700 hover:border-emerald-500/50 text-slate-400 hover:text-emerald-400 text-xs font-semibold rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
                  {compUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                  Anexar comprovante
                </label>
                {(form.comprovantes || []).map((url, i) => (
                  <span key={url} className="inline-flex items-center gap-1.5 glass rounded-full px-3 py-1.5 text-[11px] text-sky-400" data-testid={`rental-comprovante-${i}`}>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline">Comprovante {i + 1}</a>
                    <button type="button" onClick={() => setForm((fm) => ({ ...fm, comprovantes: fm.comprovantes.filter((_, j) => j !== i) }))} className="text-slate-500 hover:text-red-400" data-testid={`rental-comprovante-remove-${i}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center gap-4">
              <p className="text-sm text-slate-300" data-testid="rental-total">
                {dias} diária(s) × {formatBRL(Number(form.diaria || 0))} = <span className="font-display font-bold text-emerald-400 text-lg">{formatBRL(total)}</span>
              </p>
              <div className="flex gap-3 ml-auto">
                <button type="submit" disabled={saving} data-testid="rental-save-button" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-3 rounded-full transition-colors">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} {form.id ? "Salvar alterações" : "Criar locação"}
                </button>
                <button type="button" onClick={() => setForm(null)} className="glass text-white text-sm font-semibold px-6 py-3 rounded-full">Cancelar</button>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {checklistFor && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setChecklistFor(null)}
            data-testid="checklist-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl p-7 w-full max-w-lg max-h-[85vh] overflow-y-auto space-y-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-display text-xl font-bold text-white">
                    Checklist de {checklistFor.tipo === "retirada" ? "retirada" : "devolução"}
                  </h2>
                  <p className="text-xs text-slate-400">{vehicleName(checklistFor.rental.veiculo_id)} · {checklistFor.rental.cliente}</p>
                </div>
                <button onClick={() => setChecklistFor(null)} className="text-slate-400 hover:text-white" data-testid="checklist-close"><X className="w-5 h-5" /></button>
              </div>

              <div>
                <label className="text-xs text-slate-400 flex items-center gap-1.5 mb-2"><Fuel className="w-3.5 h-3.5 text-emerald-400" /> Nível de combustível</label>
                <div className="grid grid-cols-5 gap-2" data-testid="checklist-fuel">
                  {[[10, "Reserva"], [25, "1/4"], [50, "1/2"], [75, "3/4"], [100, "Cheio"]].map(([val, label]) => (
                    <button
                      key={val} type="button"
                      onClick={() => setChk({ ...chk, combustivel: val })}
                      data-testid={`fuel-${val}`}
                      className={`py-2.5 rounded-xl text-xs font-semibold transition-colors ${chk.combustivel === val ? "bg-emerald-500 text-emerald-950" : "glass text-slate-300 hover:text-emerald-400"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <input ref={chkFileRef} type="file" accept="image/*" multiple className="hidden" onChange={chkFiles} data-testid="checklist-file-input" />
                <button
                  type="button" onClick={() => chkFileRef.current?.click()} disabled={chkUploading}
                  data-testid="checklist-upload-button"
                  className="w-full border-2 border-dashed border-slate-700 hover:border-emerald-500/60 rounded-2xl py-6 flex flex-col items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors disabled:opacity-60"
                >
                  {chkUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                  <span className="text-xs font-semibold">{chkUploading ? "Enviando..." : "Fotos do veículo (lataria, painel, tanque)"}</span>
                </button>
                {chk.fotos.length > 0 && (
                  <div className="mt-3 grid grid-cols-4 gap-2" data-testid="checklist-fotos">
                    {chk.fotos.map((f) => (
                      <div key={f} className="relative rounded-lg overflow-hidden aspect-square">
                        <img src={f} alt="checklist" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setChk({ ...chk, fotos: chk.fotos.filter((x) => x !== f) })}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-red-400 flex items-center justify-center"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <textarea
                className={`${inputCls} min-h-[80px] resize-none`}
                placeholder="Observações (avarias, itens do carro, quilometragem...)"
                value={chk.observacao}
                onChange={(e) => setChk({ ...chk, observacao: e.target.value })}
                data-testid="checklist-obs"
              />

              <div className="flex flex-col gap-2 pt-1">
                <button
                  onClick={() => submitChecklist(false)} disabled={saving}
                  data-testid="checklist-save"
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-5 py-3.5 rounded-full transition-colors"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Salvar e {checklistFor.tipo === "retirada" ? "ativar locação" : "registrar devolução"}
                </button>
                <button
                  onClick={() => submitChecklist(true)} disabled={saving}
                  data-testid="checklist-skip"
                  className="w-full text-xs font-semibold text-slate-400 hover:text-emerald-400 py-2 transition-colors"
                >
                  {checklistFor.tipo === "retirada" ? "Ativar" : "Devolver"} sem checklist
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {finesFor && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setFinesFor(null)}
            data-testid="fines-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl p-6 w-full max-w-lg space-y-5 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Gavel className="w-5 h-5 text-amber-400" /> Multas — {vehicleName(finesFor.veiculo_id)}
                  </h2>
                  <p className="text-xs text-slate-400">{finesFor.cliente} · {fmtData(finesFor.inicio)} a {fmtData(finesFor.fim)}</p>
                </div>
                <button onClick={() => setFinesFor(null)} data-testid="fines-modal-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              <form onSubmit={addFine} className="grid grid-cols-2 gap-3" data-testid="fines-form">
                <input className={`${inputCls} col-span-2`} placeholder="Descrição da multa (ex: excesso de velocidade — Av. Brasil km 12)*" required value={fineForm.descricao} onChange={(e) => setFineForm({ ...fineForm, descricao: e.target.value })} data-testid="fine-input-descricao" />
                <input className={inputCls} type="number" step="0.01" min="0" placeholder="Valor (R$)*" required value={fineForm.valor} onChange={(e) => setFineForm({ ...fineForm, valor: e.target.value })} data-testid="fine-input-valor" />
                <input className={inputCls} type="date" required value={fineForm.data_infracao} onChange={(e) => setFineForm({ ...fineForm, data_infracao: e.target.value })} data-testid="fine-input-data" />
                <button type="submit" disabled={saving} data-testid="fine-add-button" className="col-span-2 inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-amber-950 font-bold px-4 py-3 rounded-full transition-colors text-sm">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Registrar multa e lançar cobrança
                </button>
              </form>

              {fines.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="fines-empty">Nenhuma multa registrada nesta locação.</p>
              ) : (
                <ul className="space-y-2" data-testid="fines-list">
                  {fines.map((f) => (
                    <li key={f.id} className="bg-[#0d0f17] rounded-xl px-4 py-3 space-y-2" data-testid={`fine-item-${f.id}`}>
                      <div className="flex items-center gap-2.5">
                        <button
                          onClick={() => toggleFine(f)} data-testid={`fine-toggle-${f.id}`}
                          title={f.status === "paga" ? "Marcar como pendente" : "Marcar como paga"}
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${f.status === "paga" ? "bg-emerald-500 border-emerald-500 text-emerald-950" : "border-slate-600 text-transparent hover:border-emerald-500"}`}
                        >
                          <Check className="w-3 h-3" />
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${f.status === "paga" ? "text-slate-500 line-through" : "text-white"}`}>{f.descricao}</p>
                          <p className="text-[11px] text-slate-500">infração em {fmtData(f.data_infracao)} · {f.status === "paga" ? "paga" : "pendente"}</p>
                        </div>
                        <span className="text-sm font-semibold text-amber-400 whitespace-nowrap">{formatBRL(f.valor)}</span>
                        <button onClick={() => removeFine(f.id)} data-testid={`fine-delete-${f.id}`} className="text-slate-600 hover:text-red-400 transition-colors shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {finesFor.telefone && (
                        <a
                          href={waMulta(finesFor, f)} target="_blank" rel="noopener noreferrer"
                          data-testid={`fine-wa-${f.id}`}
                          className="flex items-center justify-center gap-2 bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-400 text-xs font-semibold rounded-full py-2 transition-colors"
                        >
                          <MessageCircle className="w-3.5 h-3.5" /> Cobrar no WhatsApp (mensagem pronta)
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {rentals === null ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-20 rounded-2xl bg-[#131620] animate-pulse" />)}</div>
      ) : view === "calendario" ? (
        <div className="glass rounded-2xl p-5" data-testid="locacao-calendario">
          <div className="flex items-center justify-between mb-5">
            <button onClick={() => navMes(-1)} data-testid="cal-prev" className="w-9 h-9 rounded-full glass flex items-center justify-center text-slate-300 hover:text-emerald-400"><ChevronLeft className="w-4 h-4" /></button>
            <p className="font-display font-bold text-white capitalize" data-testid="cal-titulo">{MESES[mesNum - 1]} / {anoCal}</p>
            <button onClick={() => navMes(1)} data-testid="cal-next" className="w-9 h-9 rounded-full glass flex items-center justify-center text-slate-300 hover:text-emerald-400"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="flex flex-wrap gap-3 mb-5 text-[11px] text-slate-400">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500/70" /> Ativa</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500/40" /> Reservada</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-500/40" /> Devolvida</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#1A1D2B]" /> Livre</span>
          </div>
          {locaveis.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum veículo marcado para locação no Estoque.</p>
          ) : (
            <div className="overflow-x-auto">
              <div style={{ minWidth: 170 + diasNoMes * 30 }}>
                <div className="flex mb-2">
                  <div className="w-[170px] shrink-0" />
                  {days.map((d) => (
                    <div key={d} className={`w-[30px] shrink-0 text-center text-[10px] ${`${mesCal}-${String(d).padStart(2, "0")}` === hoje ? "text-emerald-400 font-bold" : "text-slate-500"}`}>
                      {d}
                    </div>
                  ))}
                </div>
                {locaveis.map((v) => (
                  <div key={v.id} className="flex items-center mb-1.5" data-testid={`cal-row-${v.id}`}>
                    <div className="w-[170px] shrink-0 pr-3 text-xs font-medium text-white truncate">{v.nome}</div>
                    {days.map((d) => {
                      const iso = `${mesCal}-${String(d).padStart(2, "0")}`;
                      const r = rentalNoDia(v.id, iso);
                      return (
                        <div
                          key={d}
                          title={r ? `${r.cliente} · ${STATUS[r.status].label} · ${formatBRL(r.valor_total)}` : "Livre"}
                          className={`w-[30px] shrink-0 h-8 flex items-center justify-center ${r ? STATUS[r.status].cell : "bg-[#12151f]"} ${d === 1 || (r && r.inicio === iso) ? "rounded-l-md" : ""} ${d === diasNoMes || (r && r.fim === iso) ? "rounded-r-md" : ""} ${iso === hoje ? "ring-1 ring-emerald-400/60" : ""}`}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : rentals.length === 0 ? (
        <p className="text-slate-400 text-sm glass rounded-2xl p-8" data-testid="locacao-empty">Nenhuma locação ainda. Marque um veículo como "disponível para locação" no Estoque e crie a primeira.</p>
      ) : (
        <div className="space-y-3" data-testid="locacao-list">
          {rentals.map((r) => {
            const st = STATUS[r.status] || STATUS.reservada;
            const alertasV = alertasManut[r.veiculo_id] || [];
            const vencido = alertasV.some((a) => a.status === "vencido");
            return (
              <div key={r.id} className="glass rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`rental-card-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white flex items-center gap-2 flex-wrap">
                    {vehicleName(r.veiculo_id)}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                    {alertasV.length > 0 && (
                      <span
                        title={alertasV.map((a) => `${a.tipo}: ${a.status}`).join(" · ")}
                        className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${vencido ? "bg-red-500/15 text-red-400 border-red-500/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30"}`}
                        data-testid={`rental-manutencao-${r.id}`}
                      >
                        <Wrench className="w-3 h-3" /> {vencido ? "Manutenção vencida" : "Manutenção próxima"}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    {r.cliente} {r.telefone ? `· ${r.telefone}` : ""} · {fmtData(r.inicio)} a {fmtData(r.fim)} · {r.dias} diária(s)
                  </p>
                  {(r.checklist_retirada || r.checklist_devolucao || r.comprovantes?.length > 0) && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`rental-checklists-${r.id}`}>
                      {r.comprovantes?.length > 0 && (
                        <a
                          href={r.comprovantes[0]} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[11px] glass rounded-full px-3 py-1 text-sky-400 hover:text-sky-300"
                          data-testid={`rental-comprovantes-badge-${r.id}`}
                        >
                          <Paperclip className="w-3 h-3" /> {r.comprovantes.length} comprovante(s) de pagamento
                        </a>
                      )}
                      {r.checklist_retirada && (
                        <span className="inline-flex items-center gap-1.5 text-[11px] glass rounded-full px-3 py-1 text-slate-300">
                          <Fuel className="w-3 h-3 text-emerald-400" /> Retirada {r.checklist_retirada.combustivel}%
                          <Camera className="w-3 h-3 text-emerald-400 ml-1" /> {r.checklist_retirada.fotos?.length || 0}
                        </span>
                      )}
                      {r.checklist_devolucao && (
                        <span className="inline-flex items-center gap-1.5 text-[11px] glass rounded-full px-3 py-1 text-slate-300">
                          <Fuel className="w-3 h-3 text-sky-400" /> Devolução {r.checklist_devolucao.combustivel}%
                          <Camera className="w-3 h-3 text-sky-400 ml-1" /> {r.checklist_devolucao.fotos?.length || 0}
                        </span>
                      )}
                    </div>
                  )}
                  {r.assinatura && (
                    <div className="mt-2" data-testid={`rental-assinado-${r.id}`}>
                      <span className="inline-flex items-center gap-1.5 text-[11px] bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1 text-emerald-400 font-semibold">
                        <PenLine className="w-3 h-3" /> Contrato assinado por {r.assinatura.nome} · {new Date(r.assinatura.assinado_em).toLocaleDateString("pt-BR")}
                      </span>
                    </div>
                  )}
                </div>
                <p className="font-display font-bold text-emerald-400 text-lg whitespace-nowrap" data-testid={`rental-total-${r.id}`}>{formatBRL(r.valor_total)}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => downloadContrato(r)} data-testid={`rental-contrato-${r.id}`}
                    title="Baixar contrato PDF"
                    className="w-10 h-10 rounded-full glass hover:border-emerald-500/50 flex items-center justify-center text-slate-300 hover:text-emerald-400 transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  {!r.assinatura && (r.status === "reservada" || r.status === "ativa") && (
                    <button
                      onClick={() => copiarLink(r)} data-testid={`rental-link-assinatura-${r.id}`}
                      title="Copiar link de assinatura do contrato"
                      className="w-10 h-10 rounded-full bg-sky-500/15 flex items-center justify-center text-sky-400 hover:bg-sky-500/30 transition-colors"
                    >
                      <Link2 className="w-4 h-4" />
                    </button>
                  )}
                  {r.status !== "cancelada" && (
                    <button
                      onClick={() => openFines(r)} data-testid={`rental-multas-${r.id}`}
                      title="Multas de trânsito da locação"
                      className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center text-amber-400 hover:bg-amber-500/30 transition-colors"
                    >
                      <Gavel className="w-4 h-4" />
                    </button>
                  )}
                  {r.telefone && (
                    <a href={waLink(r.assinatura
                        ? `Olá ${r.cliente}! Sobre sua locação na Seed Motors.`
                        : `Olá ${r.cliente}! Para agilizar a retirada do seu veículo na Seed Motors, assine o contrato de locação aqui: ${linkAssinatura(r)}`)}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`rental-wa-${r.id}`}
                      className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-colors" aria-label="WhatsApp">
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => openEdit(r)} data-testid={`rental-edit-${r.id}`}
                    title="Editar locação (datas, cliente, diária, comprovantes)"
                    className="w-10 h-10 rounded-full glass hover:border-emerald-500/50 flex items-center justify-center text-slate-300 hover:text-emerald-400 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeRental(r)} data-testid={`rental-delete-${r.id}`}
                    title="Excluir locação"
                    className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/25 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {r.status === "reservada" && (
                    <button onClick={() => openChecklist(r, "retirada")} data-testid={`rental-ativar-${r.id}`} className="inline-flex items-center gap-1.5 bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-400 text-xs font-semibold px-4 py-2.5 rounded-full transition-colors">
                      <Play className="w-3.5 h-3.5" /> Ativar
                    </button>
                  )}
                  {r.status === "ativa" && (
                    <button onClick={() => openChecklist(r, "devolucao")} data-testid={`rental-devolver-${r.id}`} className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-bold px-4 py-2.5 rounded-full transition-colors">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Devolver
                    </button>
                  )}
                  {(r.status === "reservada" || r.status === "ativa") && (
                    <button onClick={() => setStatus(r, "cancelada")} data-testid={`rental-cancelar-${r.id}`} className="inline-flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/25 text-red-400 text-xs font-semibold px-4 py-2.5 rounded-full transition-colors">
                      <Ban className="w-3.5 h-3.5" /> Cancelar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
